import React from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N500 } from '@atlaskit/theme/colors';
import {
  Environment,
  ENV_PRODUCTION,
  ENV_STAGING
} from '@atlassiansox/bxpkit-core';
import OriginTracing from '@atlassiansox/origin-tracing';
import styled from '@emotion/styled';

import {
  AppleLogo,
  GoogleLogo,
  MicrosoftLogo,
  SlackLogo
} from '../../utility/social-logos';
import { fontCharlieText } from '../styled';

type SocialSignUpButtonProps = {
  buttonId?: string;
  buttonText: string | JSX.Element;
  provider: 'google' | 'microsoft' | 'apple' | 'slack';
  redirectUrl?: string;
};

export function SocialSignUpButton({
  buttonId,
  buttonText,
  provider,
  redirectUrl
}: SocialSignUpButtonProps) {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const metadataId = `wac-signup-${provider}`;

  function getLoginOrigin(): string {
    const originTracingOptions: { product: string; id?: string } = {
      product: metadataId
    };
    const env = Environment.getEnvironment();
    if (env !== ENV_PRODUCTION && env !== ENV_STAGING) {
      originTracingOptions.id = 'static-uuid-for-testing';
    }
    const loginOrigin = new OriginTracing({ ...originTracingOptions });
    return loginOrigin.encode();
  }

  const continueUrl = redirectUrl
    ? new URL(redirectUrl)
    : new URL(window.location.href);

  continueUrl.searchParams.append('social-signup-login', 'true');
  continueUrl.searchParams.append('provider', provider);

  const params = new URLSearchParams({
    application: 'wac',
    continue: continueUrl.href,
    prompt: 'select_account',
    atlOrigin: encodeURIComponent(getLoginOrigin())
  });

  const urlSocialSignUpContinue = `${Environment.getIdentityBaseUrl()}/login/initiate/${provider}?${params}`;

  const analyticsCallback = () => {
    createAnalyticsEvent({
      eventName: metadataId,
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
  };

  const renderProviderLogo = (
    provider: SocialSignUpButtonProps['provider']
  ) => {
    switch (provider) {
      case 'google':
        return <GoogleLogo />;
      case 'microsoft':
        return <MicrosoftLogo />;
      case 'apple':
        return <AppleLogo />;
      case 'slack':
        return <SlackLogo />;
    }
  };

  return (
    <SocialButton
      href={urlSocialSignUpContinue}
      onClick={analyticsCallback}
      id={buttonId}
      data-testid={`simple-signup-${provider}-sign-in-button`}
      className="social-signup-button"
    >
      {renderProviderLogo(provider)}
      {buttonText}
    </SocialButton>
  );
}

const SocialButton = styled.a`
  text-decoration: none;
  height: 39px;
  width: 100%;
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${N500};
  font-weight: 500;
  box-shadow: rgba(0, 0, 0, 0.2) 1px 1px 5px 0 !important;
  background-color: #fff;
  gap: 0.5rem;
  margin: 0px 0px 16px 0px;
  padding: 0;
  border: 0;
  font-size: 100%;
  vertical-align: baseline;
  font-family: ${fontCharlieText};
  font-style: normal;

  &:focus,
  &:hover {
    background-color: #f9fafc;
    color: ${N500};
    text-decoration: none;
  }

  &:focus-visible {
    outline-offset: 1px;
  }

  svg {
    display: block;
    width: 18px;
    height: 18px;
    margin-bottom: 0; // override Magnolia styles
  }
`;
